import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { branchNameById, STATUS_LABEL } from './speakupConstants';
import './speakup.css';

// ---------------------------------------------------------------------------
// Gate stage H / decision D19 — the executive ledger.
//
// "Counts, branch, category, status, age. Zero words. Plus the ability to
// reassign the handler." Enough to notice neglect, not enough to breach
// confidence.
//
// THERE IS NO ROW CLICK ON THIS SCREEN AND THAT IS NOT AN OVERSIGHT. The
// function behind it — exec_speakup_ledger() — returns no body, no summary,
// no thread token and no submission id, so there is nothing here that could
// be used to fetch one even by a future edit to this file. The sentence at
// the top is a property of the data, not a promise about a button.
//
// D8: assigning the handler is admin-or-executive; READING submissions is the
// flag alone. Those are deliberately different permissions — an executive who
// could grant themselves reading by reassigning the handler to themselves
// would make D2 meaningless — which is why admin_reassign_handler() refuses
// to self-assign.
// ---------------------------------------------------------------------------
export default function SpeakUpLedger() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.rpc('exec_speakup_ledger').then(({ data, error: rpcError }) => {
      if (rpcError) { setError('denied'); return; }
      setRows(data || []);
    });
  }, []);

  if (error === 'denied') {
    return (
      <div className="speakup-admin-wrap">
        <div className="card"><div className="card-body">You don't have access to this section.</div></div>
      </div>
    );
  }

  const all = rows || [];
  const concerns = all.filter((r) => r.lane === 'concern' && r.status !== 'resolved');
  const overdue = concerns.filter((r) => r.is_overdue);
  const oldestWaiting = concerns.reduce(
    (m, r) => (r.awaiting_reply_days !== null && r.awaiting_reply_days > m ? r.awaiting_reply_days : m),
    0
  );

  return (
    <div className="speakup-admin-wrap">
      <div className="page-header">
        <div className="page-title">Speak Up — oversight</div>
        <div className="page-sub">
          Counts only. No submission on this screen can be opened, by anyone, including you.
        </div>
      </div>

      <div className="speakup-ledger-stats">
        <div className="speakup-stat">
          <div className="speakup-stat-n">{concerns.length}</div>
          <div className="speakup-stat-l">Concerns open</div>
        </div>
        <div className="speakup-stat">
          <div className="speakup-stat-n">{oldestWaiting > 0 ? `${oldestWaiting} days` : '—'}</div>
          <div className="speakup-stat-l">Oldest awaiting reply</div>
        </div>
        <div className={overdue.length > 0 ? 'speakup-stat speakup-stat-alert' : 'speakup-stat'}>
          <div className="speakup-stat-n">{overdue.length}</div>
          <div className="speakup-stat-l">Past the reply date</div>
        </div>
      </div>

      <div className="card">
        {rows === null && <div className="card-body">Loading…</div>}
        {rows && concerns.length === 0 && (
          <div className="card-body text-muted">No concerns are open.</div>
        )}
        {concerns.map((r, i) => (
          <div key={i} className="speakup-ledger-row">
            {r.is_overdue ? (
              <span className="speakup-age speakup-age-over">Overdue</span>
            ) : r.awaiting_reply_days !== null ? (
              <span className="speakup-age">Due in {Math.max(0, 7 - r.awaiting_reply_days)} days</span>
            ) : (
              <span className="speakup-age speakup-age-ok">Acknowledged</span>
            )}
            <span className="speakup-chip speakup-chip-cat">{r.category}</span>
            <strong>{branchNameById(r.branch_id)}</strong>
            <span className="speakup-chip speakup-chip-status">{STATUS_LABEL[r.status] || r.status}</span>
            <span className="speakup-when">Open {r.age_days} days</span>
          </div>
        ))}
      </div>

      <p className="speakup-note" style={{ marginTop: 14 }}>
        Ideas are not listed here — they are read by branch managers as written, and carry no clock.
      </p>
    </div>
  );
}
